/**
 * Whether a formula is KNOWN to return a multi-material allocation list.
 *
 * THE ONE PREDICATE (per Parvez: "allow array only when we know the formula
 * can return an array"): the BOM fan-out, the formula bindings, and the
 * multi-select material UI must all gate on this same test, so a formula is
 * list-shaped everywhere or nowhere. Detection is the paved-road token — the
 * only sanctioned list producer; if another one ever ships, extend HERE.
 */
export const isProjectionFormula = (formula: string): boolean =>
	formula.includes('packProjectedLineSegments');

/**
 * The scope binding a projection formula uses. It is injected at evaluation
 * time and bound to canvas facts — deliberately NOT a recipe parameter — so
 * parameter extraction must treat it as expected, not missing.
 *
 * Exactly ONE name, and it is the paved-road token itself. The assigned layers
 * and the selected materials are closed over by the factory instead of being
 * named in the formula, because an author cannot choose either of them — which
 * is also what makes `layers` and `materials` unresolvable in any expression.
 */
export const projectionBindingNames: readonly string[] = ['packProjectedLineSegments'];

/**
 * Whether an ingredient multi-selects: every one of its candidate materials
 * computes through a projection formula, so whichever set is selected, the
 * evaluation returns one quantity per selected material. A mixed ingredient
 * stays single-select — the formula that runs is the first selected material's,
 * so a list shape cannot be guaranteed up front.
 *
 * @param materials The ingredient's selectable materials.
 * @returns Whether the ingredient's materials may be multi-selected.
 */
export const isProjectionIngredient = (
	materials: { formulaDetails: { formula: string } }[]
): boolean =>
	materials.length > 0 &&
	materials.every((material) => isProjectionFormula(material.formulaDetails.formula));
